(() => {
  if (window.__rocketzLicenseRuntime) return;
  window.__rocketzLicenseRuntime = true;

  const script = document.currentScript;
  const designMode = script?.dataset.designMode === "true";
  const themeBuild = script?.dataset.themeBuild || "unknown";
  const shop = window.Shopify?.shop || window.location.hostname;
  const cacheKey = `rz-license-v4:${shop}`;
  const dailyCheckEvery = 86400000;
  const affiliateCacheKey = "rz-affiliate-v1";
  const affiliateCacheTtl = 10368000000;
  let checking = false;
  let pollTimer;

  const captureAffiliateAttribution = async () => {
    const params = new URLSearchParams(window.location.search);
    const queryReferral = params.get("rz_ref")?.trim().toUpperCase() || "";
    const queryLink = params.get("rz_link")?.trim() || "";
    const queryTimestamp = Number(params.get("rz_at"));
    const validReferral = /^[A-Z0-9_-]{3,48}$/.test(queryReferral);
    const validLink = /^[A-Za-z0-9_-]{3,48}$/.test(queryLink);
    let attribution = null;

    if (validReferral && validLink) {
      attribution = {
        referral: queryReferral,
        link: queryLink,
        clickedAt: Number.isFinite(queryTimestamp) && queryTimestamp > 0 ? queryTimestamp : Date.now(),
        expiresAt: Date.now() + affiliateCacheTtl,
      };
      try {
        localStorage.setItem(affiliateCacheKey, JSON.stringify(attribution));
      } catch {}
      params.delete("rz_ref");
      params.delete("rz_link");
      params.delete("rz_at");
      const cleanQuery = params.toString();
      history.replaceState(history.state, "", `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ""}${window.location.hash}`);
    } else {
      try {
        attribution = JSON.parse(localStorage.getItem(affiliateCacheKey) || "null");
      } catch {
        attribution = null;
      }
    }

    const validCachedAttribution =
      attribution &&
      /^[A-Z0-9_-]{3,48}$/.test(attribution.referral || "") &&
      /^[A-Za-z0-9_-]{3,48}$/.test(attribution.link || "") &&
      Number(attribution.expiresAt) > Date.now();
    if (!validCachedAttribution) {
      try {
        localStorage.removeItem(affiliateCacheKey);
      } catch {}
      return;
    }

    const syncKey = `rz-affiliate-synced:${attribution.referral}:${attribution.link}`;
    try {
      if (sessionStorage.getItem(syncKey) === "1") return;
    } catch {}

    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: {
            _rocketz_affiliate: attribution.referral,
            _rocketz_affiliate_link: attribution.link,
            _rocketz_affiliate_at: String(attribution.clickedAt),
          },
        }),
      });
      if (response.ok) sessionStorage.setItem(syncKey, "1");
    } catch {}
  };

  let memoryState;
  const readCache = () => {
    if (memoryState) return memoryState;
    try {
      const value = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (value?.shop === shop && typeof value.active === "boolean") memoryState = value;
    } catch {}
    return memoryState || null;
  };
  const saveCache = (state) => {
    memoryState = state;
    try { localStorage.setItem(cacheKey, JSON.stringify(state)); } catch {}
  };
  const manuallyRevoked = (state) => state?.active === false && state?.manuallyRevoked === true &&
    ["manual_revoked", "license_revoked"].includes(state.reason);
  const apply = (state) => {
    const blocked = manuallyRevoked(state);
    document.documentElement.dataset.rocketzLicense = blocked ? "inactive" : "active";
    document.querySelector(".rz-license-blocked-screen")?.remove();
    if (blocked) {
      const screen = document.createElement("main");
      screen.className = "rz-license-blocked-screen";
      screen.setAttribute("role", "main");
      const content = document.createElement("div");
      content.className = "rz-license-blocked-screen__content";
      const title = document.createElement("h1");
      title.textContent = "Acesso ao tema revogado";
      const message = document.createElement("p");
      message.textContent = "O administrador Rocketz revogou manualmente o acesso desta loja. Entre em contato com o suporte.";
      content.append(title, message);
      screen.appendChild(content);
      document.body.appendChild(screen);
    }
    document.dispatchEvent(new CustomEvent("rocketz:license", { detail: { ...state, active: !blocked } }));
  };
  const age = (state) => Date.now() - Number(state?.attemptedAt || state?.checkedAt || 0);
  const isFresh = (state) => state && Number.isFinite(age(state)) && age(state) >= 0 && age(state) < dailyCheckEvery;
  const scheduleCheck = (delay = dailyCheckEvery) => {
    clearTimeout(pollTimer);
    pollTimer = setTimeout(checkLicense, delay);
  };
  const checkLicense = async () => {
    if (checking) return;
    const previous = readCache();
    if (isFresh(previous)) { scheduleCheck(dailyCheckEvery - age(previous)); return; }
    if (document.visibilityState === "hidden") { scheduleCheck(); return; }
    checking = true;
    const attemptedAt = Date.now();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const query = new URLSearchParams({ theme_build: themeBuild, _rz: String(attemptedAt) });
      const response = await fetch(`/apps/rocketz-license?${query}`, {
        credentials: "same-origin", headers: { Accept: "application/json" },
        cache: "no-store", signal: controller.signal,
      });
      if (!response.ok) throw new Error("verification_unavailable");
      const payload = await response.json();
      const revoked = manuallyRevoked(payload);
      if (!payload || (!revoked && (payload.active !== true || payload.reason !== "ok"))) {
        throw new Error("unconfirmed_license");
      }
      const state = {
        shop, active: !revoked, reason: revoked ? "manual_revoked" : "ok", manuallyRevoked: revoked,
        reviewRequired: payload.reviewRequired === true,
        entitlements: Array.isArray(payload.entitlements) ? payload.entitlements : [],
        checkedAt: attemptedAt, attemptedAt,
      };
      saveCache(state);
      apply(state);
    } catch {
      // Expiry, missing confirmation and technical failures cannot suspend a theme.
      // A previous HUMAN revocation remains effective until a confirmed restoration.
      const state = {
        ...previous, shop, active: !manuallyRevoked(previous),
        manuallyRevoked: manuallyRevoked(previous),
        reason: manuallyRevoked(previous) ? "manual_revoked" : "awaiting_admin_review",
        reviewRequired: true, entitlements: previous?.entitlements || [], attemptedAt,
      };
      saveCache(state);
      apply(state);
    } finally {
      clearTimeout(timeout);
      checking = false;
      scheduleCheck();
    }
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !isFresh(readCache())) checkLicense();
  });
  window.addEventListener("pageshow", () => { if (!isFresh(readCache())) checkLicense(); });
  window.addEventListener("online", () => { if (!isFresh(readCache())) checkLicense(); });
  const cached = readCache();
  apply(cached || { shop, active: true, reason: "awaiting_daily_check", entitlements: [] });
  if (isFresh(cached)) scheduleCheck(dailyCheckEvery - age(cached));
  else checkLicense();

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(captureAffiliateAttribution, { timeout: 3000 });
  } else {
    window.setTimeout(captureAffiliateAttribution, 1000);
  }
})();
